/// <amd-module name="Wit/BacklogsHub/MappingPane/MappingPane" />
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableValue, ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssLayoutManager } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Header } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { BoltListDragEvent, IListDropData, ListDropTargetBehavior } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, ITableRow, SimpleTableCell, Table, TableColumnStyle } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { Content } from "Wit/BacklogsHub/Common/BacklogHubContants";
import { BacklogLevelWorkItemData } from "Wit/BacklogsHub/Common/Contracts/BacklogsHub.Models";
import { IBacklogLevelWorkItemService } from "Wit/BacklogsHub/Common/Services/BacklogLevelWorkItemService";
import * as Resources from "Wit/BacklogsHub/Resources";
import { WorkItem, WorkItemTypeColorAndIcon } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { BacklogLevelConfiguration } from "Wit/Clients/Work/Contracts/Work";
import { IBacklogTreeRow } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemConfigurationService } from "Wit/Common/BacklogContentService/BacklogWorkItemConfigurationService";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { IBacklogsHubContentService } from "Wit/Common/BacklogContentService/IBacklogsHubContentService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { ICurrentDirectoryService } from "Wit/Common/CurrentDirectoryService/ICurrentDirectoryService";
import { CoreFieldRefNames, OobFieldRefNames } from "Wit/Common/Generated/Constants";
import { FetchWorkItemsDialog } from "Wit/Common/Utils/WorkItemUtils/FetchWorkItemsDialog";
import { restoreSelection } from "Wit/Common/WorkItemDialogs/Components/ProcessWorkItemsDialog";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { IDirectoryDropdownItem } from "Wit/DirectoryService/Models";
import { DirectoryDropdown } from "Wit/DirectoryView/Components/DirectoryDropdown";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { IBacklogsDirectoryService } from "../Common/Services/IBacklogsDirectoryService";

const shimmerCount = 5;

export interface IMappingPaneProps {
    targetBacklogLevel: IObservableValue<BacklogLevelConfiguration | undefined>;
    visibleBacklogLevels: BacklogLevelConfiguration[];
    onDismiss: () => void;
}

export class MappingPane extends TfsComponent<IMappingPaneProps> {
    private backlogLevelWorkItemService: IBacklogLevelWorkItemService;
    private backlogContentService: ICommonBacklogContentService;
    private currentDirectoryService: ICurrentDirectoryService;
    private backlogWorkItemConfigurationService: IBacklogWorkItemConfigurationService;
    private backlogWorkItemService: IBacklogWorkItemService;
    private dropBehavior = new ListDropTargetBehavior<ITreeItemEx<IBacklogTreeRow>>({
        type: "backlog-item",
        id: "backlog-mapping-pane",
        onDrop: (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData) => this.onDrop(event, dropData),
        allowedTypes: ["backlog-item"],
        isTree: true
    });
    private itemProvider: DirectoryDropdownProvider;
    private selection: ObservableValue<DirectoryItem | undefined>;
    private workItems = new ObservableArray<WorkItem | ObservableValue<WorkItem | undefined>>([]);
    private showZeroData = new ObservableValue<boolean>(false);
    private useChangeParentPerfomanceOptimization: boolean;

    constructor(props: IMappingPaneProps, context: IVssComponentContext) {
        super(props, context);
        const directoryService = context.pageContext.getService<IBacklogsDirectoryService>("IBacklogsDirectoryService");

        // Hide actions, favorites, and groups in the item provider
        this.itemProvider = new DirectoryDropdownProvider({
            directoryService,
            artifactTypeName: Resources.Backlogs,
            iconProps: { iconName: "People", ariaHidden: true },
            hideActions: true,
            hideFavorites: true,
            hideGroups: true
        });
        this.backlogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.backlogLevelWorkItemService = this.context.pageContext.getService<IBacklogLevelWorkItemService>("IBacklogLevelWorkItemService");
        this.currentDirectoryService = this.context.pageContext.getService<ICurrentDirectoryService>("ICurrentDirectoryService");
        this.backlogWorkItemConfigurationService = this.context.pageContext.getService<IBacklogWorkItemConfigurationService>(
            "IBacklogWorkItemConfigurationService"
        );
        this.selection = new ObservableValue(this.currentDirectoryService.getCurrentDirectory().value);
        this.useChangeParentPerfomanceOptimization = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlags.UseChangeParentPerfomanceOptimization,
            false
        );

        if (this.useChangeParentPerfomanceOptimization) {
            this.backlogWorkItemService = this.context.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
        }

        // Adding true to ignoreCache allows pane to be updated if parents are added, can remove if perf issue later.
        this.refreshWorkItems(true);
    }

    public componentDidMount(): void {
        this.props.targetBacklogLevel.subscribe(this.onTargetBacklogLevelChanged);
    }

    public componentWillUnmount(): void {
        this.props.targetBacklogLevel.unsubscribe(this.onTargetBacklogLevelChanged);
    }

    public render(): JSX.Element {
        return (
            <Observer targetBacklogLevel={this.props.targetBacklogLevel}>
                {(observedProps: { targetBacklogLevel: BacklogLevelConfiguration | undefined }) => {
                    if (observedProps.targetBacklogLevel !== undefined) {
                        const backlogLevelName: string = observedProps.targetBacklogLevel.name;
                        const showZeroDataAction: boolean = this.isBacklogLevelVisible(observedProps.targetBacklogLevel);
                        return (
                            <React.Fragment>
                                <Header
                                    className="mapping-pane-header"
                                    title={Resources.Mapping}
                                    description={format(Resources.MappingHeaderDescription, backlogLevelName)}
                                    commandBarItems={this.getHeaderCommandBarItems()}
                                />
                                <div className="padding-left-16 flex-row flex-center">
                                    <span className="body-l">{format(Resources.MappingBodyDescription, backlogLevelName)}</span>
                                    <Observer selectedDirectory={this.selection}>
                                        {(observedProps: { selectedDirectory: DirectoryItem | undefined }) => {
                                            return (
                                                <DirectoryDropdown
                                                    itemProvider={this.itemProvider}
                                                    selectedItem={observedProps.selectedDirectory}
                                                    onSelect={this.onSelectDirectory}
                                                    hideFavorites={true}
                                                    hideIcon={true}
                                                    calloutWidth={350}
                                                />
                                            );
                                        }}
                                    </Observer>
                                    <div className="flex-grow flex-row justify-end padding-right-16">
                                        <Button
                                            ariaLabel={Resources.RefreshWorkItemsLabel}
                                            iconProps={{ iconName: "Refresh" }}
                                            onClick={() => this.refreshWorkItems(true)}
                                        />
                                    </div>
                                </div>
                                <Observer showZeroData={this.showZeroData}>
                                    {(observedProps: { showZeroData: boolean }) => {
                                        if (observedProps.showZeroData) {
                                            return (
                                                <ZeroData
                                                    imagePath={getAssetLocation(this.context.pageContext, Content.NoWorkZeroDataSvg)}
                                                    imageAltText={format(Resources.MappingZeroDataPrimaryText, backlogLevelName)}
                                                    primaryText={format(Resources.MappingZeroDataPrimaryText, backlogLevelName)}
                                                    actionText={
                                                        showZeroDataAction
                                                            ? format(Resources.MappingZeroDataSecondaryText, backlogLevelName)
                                                            : undefined
                                                    }
                                                    actionType={showZeroDataAction ? ZeroDataActionType.ctaButton : undefined}
                                                    onActionClick={showZeroDataAction ? this.onZeroDataActionClick : undefined}
                                                />
                                            );
                                        } else {
                                            return (
                                                <div className="padding-16 flex-grow v-scroll-hidden">
                                                    <Card className="bolt-table-card full-height">
                                                        <Table
                                                            behaviors={[this.dropBehavior]}
                                                            columns={this.getWorkItemTableColumns()}
                                                            className="mapping-pane-links"
                                                            itemProvider={this.workItems}
                                                            onActivate={this.onWorkItemRowClicked}
                                                            showHeader={false}
                                                            showLines={false}
                                                            virtualize={false}
                                                        />
                                                    </Card>
                                                </div>
                                            );
                                        }
                                    }}
                                </Observer>
                            </React.Fragment>
                        );
                    }

                    return null;
                }}
            </Observer>
        );
    }

    private onDrop = async (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData): Promise<void> => {
        const workItem: IBacklogTreeRow = event.detail.dataTransfer.data?.underlyingItem?.data;
        if (this.useChangeParentPerfomanceOptimization) {
            await this.fetchSelectedItems();
        }
        const workItemIds: number[] = this.backlogContentService.getSelectedItems(workItem).map(items => items.id);
        const parent = this.workItems.value[dropData.index] as WorkItem;

        if (!this.useChangeParentPerfomanceOptimization) {
            this.backlogWorkItemService = this.context.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
        }

        const backlogHubService = this.context.pageContext.getService<IBacklogsHubContentService>("IBacklogsHubContentService");
        this.backlogWorkItemService.changeWorkItemParentLink(workItemIds, parent.id, this.useChangeParentPerfomanceOptimization).then(() => {
            backlogHubService.refreshTree({
                keepOpen: true,
                filterManager: this.backlogWorkItemService.getFilterManager()
            });
        });
    };

    private openDialog(callout: (onDismiss: () => void) => JSX.Element): void {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(callout);
    }

    private fetchSelectedItems = async (): Promise<void> => {
        const notFetchedWorkItemIdPromise = this.backlogContentService.getSelectedItemsIdFromRawBacklogTree(true, this.useChangeParentPerfomanceOptimization);
        const selection = this.backlogContentService.getTreeSelection();
        const selectionCopy = selection.value.map(item => ({ ...item }));
        const notFetchedWorkItemIds = await notFetchedWorkItemIdPromise;
        await new Promise(resolve => {
            this.openDialog(onDismiss => (
                <FetchWorkItemsDialog
                    onDismiss={() => {
                        onDismiss();
                        const selectedItems = this.backlogContentService.getSelectedItems().map(item => ({
                            value: item,
                            id: item.value.coreColumnMap[CoreFieldRefNames.Id],
                            teamProject: item.value.coreColumnMap[CoreFieldRefNames.TeamProject] as string,
                            ...item.value.coreColumnMap
                        }));
                        resolve(selectedItems);
                    }}
                    processWorkItems={async workItemIds => {
                        await this.backlogWorkItemService.fetchWorkItems(workItemIds);
                        return true;
                    }}
                    workItemIds={notFetchedWorkItemIds}
                    callBack={() => {
                        restoreSelection(selectionCopy, selection);
                    }}
                />
            ));
        });
    };

    private renderWorkItemNameCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<WorkItem>,
        tableItem: WorkItem
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={`col-${columnIndex} row-${rowIndex}`}>
                <Observer workItemTypeColorAndIcons={this.backlogWorkItemConfigurationService.getObservableWorkItemTypeColorAndIcons()}>
                    {(observedProps: { workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[] }) => {
                        const workItemTypeColorAndIcon = observedProps.workItemTypeColorAndIcons.find(
                            (colorAndIcon: WorkItemTypeColorAndIcon) =>
                                colorAndIcon.workItemTypeName === tableItem.fields[CoreFieldRefNames.WorkItemType]
                        );
                        return <WorkItemTypeIcon data={workItemTypeColorAndIcon} />;
                    }}
                </Observer>
                <Tooltip overflowOnly={true}>
                    <span className="padding-left-4">{tableItem.fields[CoreFieldRefNames.Title]}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    };

    private getWorkItemTableColumns = (): ITableColumn<WorkItem>[] => {
        return [
            {
                id: "name",
                width: new ObservableValue(-100),
                columnStyle: TableColumnStyle.Primary,
                renderCell: this.renderWorkItemNameCell
            }
        ];
    };

    private onWorkItemRowClicked = (event: React.SyntheticEvent<HTMLElement>, row: ITableRow<WorkItem>) => {
        const layoutService = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutService.renderCallout(onDismiss => {
            return <WorkItemFormDialog onDismiss={onDismiss} workItemId={row.data.id} />;
        });
    };

    private isBacklogLevelVisible(backlogLevel: BacklogLevelConfiguration): boolean {
        return this.props.visibleBacklogLevels?.findIndex(b => b.id === backlogLevel.id) !== -1;
    }

    private getHeaderCommandBarItems(): IHeaderCommandBarItem[] {
        return [
            {
                ariaLabel: Resources.Close,
                iconProps: { iconName: "Clear" },
                id: "mapping-pane-close-button",
                role: "button",
                onActivate: this.props.onDismiss,
                subtle: true
            }
        ];
    }

    private onSelectDirectory = (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<IDirectoryDropdownItem>): void => {
        if (item.data?.directory) {
            this.selection.value = item.data.directory;
            this.refreshWorkItems();
        }
    };

    private onTargetBacklogLevelChanged = (): void => {
        this.refreshWorkItems();
    };

    private refreshWorkItems(ignoreCache: boolean = false): void {
        if (this.selection.value !== undefined && this.props.targetBacklogLevel.value !== undefined) {
            this.showZeroData.value = false;
            this.workItems.value = new Array<ObservableValue<WorkItem | undefined>>(shimmerCount).fill(new ObservableValue(undefined));

            this.backlogLevelWorkItemService
                .getBacklogLevelWorkItemData(this.selection.value.id, this.props.targetBacklogLevel.value.id, ignoreCache)
                .then(this.handleWorkItemResponse);
        }
    }

    private pageMoreWorkItems(teamId: string, backlogId: string, workItemIds: number[]): void {
        this.backlogLevelWorkItemService.getBacklogLevelWorkItemDataForIds(teamId, backlogId, workItemIds).then(this.handleWorkItemResponse);
    }

    private handleWorkItemResponse = (data: BacklogLevelWorkItemData | undefined): void => {
        // Ensure the web response is still relevant to the view
        if (data && data.teamId === this.selection.value?.id && data.backlogId === this.props.targetBacklogLevel.value?.id) {
            // Remove any shimmer rows
            this.workItems.removeAll((item: WorkItem | ObservableValue<WorkItem | undefined>) => ObservableLike.isObservable(item));

            if (data.workItems) {
                this.workItems.push(
                    ...data.workItems.sort((a, b) => {
                        let aRank = a.fields[OobFieldRefNames.StackRank];
                        let bRank = b.fields[OobFieldRefNames.StackRank];
                        if (aRank === undefined || aRank === null) {
                            aRank = Number.MAX_SAFE_INTEGER;
                        }
                        if (bRank === undefined || bRank === null) {
                            bRank = Number.MAX_SAFE_INTEGER;
                        }
                        let result = aRank - bRank;
                        if (result === 0) {
                            result = a.id - b.id;
                        }
                        return result;
                    })
                );
            }

            if (data.nextWorkItemsToFetch && data.nextWorkItemsToFetch.length > 0) {
                const shimmer = new Array<ObservableValue<WorkItem | undefined>>(shimmerCount).fill(new ObservableValue(undefined));
                this.workItems.push(...shimmer);
                this.pageMoreWorkItems(data.teamId, data.backlogId, data.nextWorkItemsToFetch);
            } else if (this.workItems.length === 0) {
                this.showZeroData.value = true;
            }
        }
    };

    private onZeroDataActionClick = (): void => {
        const backlogHubService = this.context.pageContext.getService<IBacklogsHubContentService>("IBacklogsHubContentService");
        const newLevel = this.props?.targetBacklogLevel?.value?.name;

        if (newLevel) {
            const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            const state = historyService.getState();
            state["backlogLevel"] = newLevel;
            historyService.pushState(state);

            backlogHubService.getBacklogLevel().value = newLevel;
        }
    };
}
